import { Component, input, model, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { User } from '../../../models/user.model';
import { Warehouse } from '../../../models/warehouse.model';
import type { AppLanguage, AppTheme } from '../topbar.types';

/** Un contact d'urgence saisi dans la fenetre de profil (modifie sur place par les champs). */
export interface ProfileContact {
  fullName: string;
  phoneNumber: string;
  relationshipId: string | number;
}

/**
 * Ce que la fenetre demande a la barre : l'initiale de l'avatar, la liste des entrepots (une
 * lecture qui rend un nouveau tableau a chaque appel, d'ou un acces par le port plutot qu'une
 * entree) et l'entrepot courant, lu seulement quand la liste n'est pas vide, comme avant.
 */
export interface TopbarProfileModalPort {
  readonly availableWarehouses: Warehouse[];
  readonly warehouseService: { currentWarehouseId(): number | null };
  getProfileInitials(): string;
}

/**
 * Fenetre « Profil » de la barre du haut : photo, email, telephone, langue, theme, entrepot,
 * contacts.
 *
 * Presentationnel : email, telephone et ouverture des deux listes sont des `model` (la barre
 * les remet aussi a zero a la fermeture) ; `profileContacts` est l'objet de la barre, modifie
 * sur place par les champs ; chaque action est une sortie que la barre execute.
 */
@Component({
  selector: 'app-topbar-profile-modal',
  standalone: true,
  imports: [NgClass, FormsModule, TranslateModule],
  templateUrl: './topbar-profile-modal.component.html',
  styleUrls: [
    '../styles/topbar-partage-01.css',
    '../styles/topbar-partage-03.css',
    '../styles/topbar-partage-04.css',
    '../styles/topbar-profile-modal-01.css',
  ],
  styles: [':host { display: contents; }'],
})
export class TopbarProfileModalComponent {
  readonly profileEmail = model<string>('');
  readonly profilePhone = model<string>('');
  readonly pmLangOpen = model<boolean>(false);
  readonly pmThemeOpen = model<boolean>(false);

  readonly showProfileModal = input.required<boolean>();
  readonly profileAvatarUrl = input.required<string>();
  readonly currentUser = input.required<User | null>();
  readonly selectedCompany = input.required<string | null>();
  readonly currentLanguage = input.required<AppLanguage | undefined>();
  readonly languages = input.required<AppLanguage[]>();
  readonly currentLang = input.required<string>();
  readonly activeTheme = input.required<string>();
  readonly activeThemeLabel = input.required<string>();
  readonly themes = input.required<AppTheme[]>();
  readonly profileContacts = input.required<ProfileContact[]>();
  readonly parentalLinks = input.required<{ id: number; label: string; code?: string }[]>();
  readonly profileSaving = input.required<boolean>();
  readonly port = input.required<TopbarProfileModalPort>();

  readonly closed = output<void>();
  readonly photoSelected = output<Event>();
  readonly languageSelected = output<string>();
  readonly themeSelected = output<string>();
  readonly warehouseCleared = output<void>();
  readonly warehouseSelected = output<Warehouse>();
  readonly contactAdded = output<void>();
  readonly contactRemoved = output<number>();
  readonly saved = output<void>();
}
